import React from 'react';

import Heading from '@atlaskit/heading';
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import VisuallyHidden from '@atlaskit/visually-hidden';

const shortcutPillStyles = xcss({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	backgroundColor: 'color.background.neutral',
	borderRadius: 'radius.small',
	paddingBlock: 'space.025',
	paddingInline: 'space.075',
});

type ShortcutHintProps = {
	shortcut: string;
	description: string;
};

const ShortcutHint = ({ shortcut, description }: ShortcutHintProps) => (
	<Inline alignBlock="center" space="space.100">
		<Box as="span" xcss={shortcutPillStyles}>
			<Text as="span" size="small" weight="medium" color="color.text.subtle">
				{shortcut}
			</Text>
		</Box>
		<Text as="span" size="small" color="color.text.subtle">
			{description}
		</Text>
	</Inline>
);

const footerLayoutStyles = xcss({
	display: 'flex',
	width: '100%',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 'space.200',
	flexWrap: 'wrap',
	borderBlockStartWidth: 'border.width',
	borderBlockStartStyle: 'solid',
	borderBlockStartColor: 'color.border',
	paddingBlock: 'space.150',
	paddingInline: 'space.200',
});

export type SearchModalFooterProps = {
	onClose(): void;
};

/**
 * __Search modal footer__
 *
 * Footer for the search modal, with shortcut hints for navigating,
 * selecting, and closing the search results.
 */
export const SearchModalFooter = (): React.JSX.Element => {
	return (
		<Box xcss={[footerLayoutStyles]} padding="space.300">
			<div aria-hidden="true">
				<Inline alignBlock="center" space="space.200">
					<ShortcutHint shortcut="↑↓" description="Navigate" />
					<ShortcutHint shortcut="↵" description="Select" />
					<ShortcutHint shortcut="Esc" description="Close" />
				</Inline>
			</div>
			<VisuallyHidden>
				<Heading size="small" as="h2">
					Keyboard instructions
				</Heading>
				<ul>
					<li>Press Up or Down arrow to navigate items</li>
					<li>Press Enter to select an item</li>
					<li>Press Escape to close the search dialog</li>
				</ul>
			</VisuallyHidden>
		</Box>
	);
};
